import * as React from "react";
import styled from "../../../styled/index";
import { CS } from "../../../styles/index";
import Transition from "react-transition-group/Transition";
import { FaTimes } from "react-icons/fa";
import shortid from "shortid";
import {MouseEventHandler} from "react";

declare global {
  interface Window {
    activeModals: string[];
  }
}

export interface ModalProps {
  id?: string;
  active: boolean;
  width: ThemeContentSizes | number;
  close: () => void;
  closeButton?: boolean;
  wrapperClick?: MouseEventHandler<HTMLDivElement>;
  wrapperStyle?: React.CSSProperties;
  children?: React.ReactNode | React.ReactNode[];
  level?: number;
  offsetCenterHorizontal?: number;
  preventClose?: boolean;
  alignTop?: boolean;
  persistChildren?: boolean;
  scrollRoot?: HTMLElement;
}

const Wrapper = styled("div")<{ active: boolean; level?: number; }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  padding: 35px 20px;
  transition: 0.23s all;
  background: rgba(0,0,0,0.7);
  overflow-y: ${({active, level}) => (active || (level || 1) > 1) ? "scroll" : "hidden"};
`;

const InnerWrapper = styled("div")`
  position: fixed;
  top: 0;
  left: 0;
  right: 15px;
  bottom: 0;
  z-index: 0;
  background: transparent;
  @media (min-width: 1200px) {
    margin-right: 20px;
  }
`;

const Main = styled("div")<ModalProps>`
  position: relative;
  background: ${(props) => props.theme.box.background};
  color: ${(props) => props.theme.box.text};
  border-radius: 3px;
  margin: ${({alignTop}) => alignTop ? "50px auto auto auto" : "auto"};
  width: 100%;
  max-width: ${({width, theme}) => typeof width === "number" ? width : theme.content_width[width]}px;
  transform: translateY(55px);
  transition: 0.23s all;
  &.active {
    transform: initial;
  }
  ${CS.shadow.med.two};
  ${({offsetCenterHorizontal}) => offsetCenterHorizontal && `transform: translateX(${offsetCenterHorizontal}px)`};
  > * {
    &:last-child {
      border-bottom-right-radius: 3px;
      border-bottom-left-radius: 3px;
    }
  }
`;
const CloseButton = styled("div")`
  ${CS.flex.center};
  cursor: pointer;
  position: absolute;
  top: -10px;
  right: -10px;
  height: 32px;
  width: 32px;
  border-radius: 30px;
  background: ${(props) => props.theme.box.text};
  color: ${(props) => props.theme.box.background};
  z-index: 1;
`;

const modalTransition = {
  entering: {
    display: "flex",
    opacity: 0,
  },
  entered:  {
    display: "flex",
    opacity: 1,
  },
  exiting: {
    display: "flex",
    opacity: 0,
  },
  exited: {
    display: "none",
    opacity: 0,
  },
};

export class Modal extends React.PureComponent<ModalProps> {

  id: string;
  previousScroll: number = 0;

  constructor(props: ModalProps) {
    super(props);
    this.id = props.id || shortid.generate();
  }

  componentDidMount() {
    this.forceUpdate();
    if (this.props.active) {
      this.onEnter();
    }
  }

  onEnter = () => {
    if (typeof window === "undefined") return;

    window.activeModals = window.activeModals || [];

    const root = this.props.scrollRoot || document.getElementById("scroll-root")!;
    if (window.activeModals.length === 0 && root.className.indexOf("modal-active") === -1) {
      const scrollBarWidth = window.innerWidth - root.clientWidth;
      this.previousScroll = Math.round(root.scrollTop || 0);
      root.classList.add("modal-active");
      root.style.overflowY = "hidden";
      root.style.marginRight = `${scrollBarWidth}px`;
    }

    window.activeModals.push(this.id);

    // SCROLL TOP
    const modalWrapper = document.getElementById(this.id)!;
    setTimeout(() => modalWrapper.scroll({ top: 0, left: 0, behavior: "auto" }), 50);

    setTimeout(() => {
      const modalContent = document.getElementById(`${this.id}-content`);
      if (modalContent) {
        modalContent.classList.add("active");
      }
      else {
        setTimeout(() => {
          const mc = document.getElementById(`${this.id}-content`);
          if (mc) {
            mc.classList.add("active");
          }
        }, 100);
      }
    }, 100);

  }

  onExit = () => {
    if (typeof window === "undefined") return;
    window.activeModals = window.activeModals || [];
    window.activeModals.splice(window.activeModals.findIndex((v) => v === this.id), 1);
    const root = this.props.scrollRoot || document.getElementById("scroll-root")!;
    if (window.activeModals.length === 0 && root.className.indexOf("modal-active") !== -1) {
      root.classList.remove("modal-active");
      root.style.overflowY = "auto";
      root.style.marginRight = "0px";
    }

    const modalContent = document.getElementById(`${this.id}-content`);
    if (modalContent) {
      modalContent.classList.remove("active");
    }
  }

  render() {
    const props = this.props;
    const { preventClose, persistChildren, wrapperClick, wrapperStyle, active } = props;
    const showCloseButton = props.closeButton === undefined ? true : props.closeButton;
    return (
      <Transition key="modal" in={active} timeout={150}>
        {(state) => {
          if (state === "entering")
            this.onEnter();
          else if (state === "exiting")
            this.onExit();
          return (
            <Wrapper
              id={this.id}
              level={props.level}
              active={active}
              onClick={wrapperClick}
              style={{ ...wrapperStyle, ...modalTransition[state as keyof typeof modalTransition] }}>
              <InnerWrapper
                id={this.id + "inner"}
                onMouseDown={(e) => {
                  if (state === "entered" && !preventClose && e.target) {
                    const t = e.target as HTMLDivElement;
                    console.log(t.offsetLeft, t.clientLeft);
                    if (t.id && t.id === (this.id + "inner")) {
                      props.close();
                    }
                  }
                }}
              />
              {(persistChildren || (state !== "exited" && state !== "exiting")) && (
                <Main {...props} id={`${this.id}-content`}>
                  {showCloseButton && (
                    <CloseButton onClick={props.close}>
                      <FaTimes/>
                    </CloseButton>
                  )}
                  {props.children}
                </Main>
              )}
            </Wrapper>
          );
        }}
      </Transition>
    );
  }

}
